import { useState } from "react";
import { OfficialChecklistForm } from "@ambulance/shared";
import { buildInitialChecklist } from "../lib/initialChecklist";
import { FileText, Loader2 } from "lucide-react";
import { apiUrl } from "../auth";

// Blank ambulance checklists (no data) rendered in the official form layout,
// for downloading as PDF templates. Public route — no login needed. Uses the
// same server-side PDF route as the real report, so they get the identical
// document-control footer (HMG/QID/20428-E2 + page labels) on every page.
//
// Two variants, matching the two ways the form is used in the field:
//   • Daily   — the per-item column is Remarks.
//   • Monthly — on the 1st of each month the same column collects the
//               nearest expiry date for every item.
const blank = {
  ...buildInitialChecklist(),
  date: "",
  hospitalName: "",
  images: [] as { storageId: string; label: string; url?: string }[],
};

const VARIANTS = [
  {
    key: "daily" as const,
    tab: "Daily",
    title: "Daily Checklist",
    hint: "Per-item column is Remarks — used every day except the 1st.",
    // Drives the per-item column in OfficialChecklistForm (no date to infer
    // the mode from on a blank template).
    expiryMode: false,
    file: "Blank Ambulance Checklist (Daily).pdf",
  },
  {
    key: "monthly" as const,
    tab: "Monthly — 1st of month",
    title: "Monthly Checklist",
    hint: "Per-item column collects the Nearest Expiry Date.",
    expiryMode: true,
    file: "Blank Ambulance Checklist (Monthly).pdf",
  },
];

export function BlankChecklistPage() {
  const [variantKey, setVariantKey] = useState<"daily" | "monthly">("daily");
  const [busy, setBusy] = useState<string | null>(null);

  const variant = VARIANTS.find((v) => v.key === variantKey)!;
  const doc = { ...blank, expiryMode: variant.expiryMode };

  async function download(v: (typeof VARIANTS)[number]) {
    if (busy) return;
    setBusy(v.key);
    try {
      const res = await fetch(`${apiUrl}/api/v1/checklists/blank-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...blank, expiryMode: v.expiryMode }),
      });
      if (!res.ok) throw new Error("PDF request failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = v.file;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      window.print(); // fallback
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="min-h-screen bg-slate-100 py-6 px-3">
      <div className="no-print max-w-4xl mx-auto mb-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              Blank Ambulance Checklist
            </h1>
            <p className="text-xs text-slate-500">
              Download an empty template as PDF.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {VARIANTS.map((v) => (
              <button
                key={v.key}
                onClick={() => download(v)}
                disabled={!!busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--hmg-red)] text-white hover:bg-[var(--hmg-red-dark)] text-sm font-semibold flex-shrink-0 disabled:opacity-60"
              >
                {busy === v.key ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                {busy === v.key ? "Preparing…" : v.title.replace(" Checklist", "")}
              </button>
            ))}
          </div>
        </div>

        {/* Preview switcher — the download buttons above work regardless. */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Preview
          </span>
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5">
            {VARIANTS.map((v) => (
              <button
                key={v.key}
                onClick={() => setVariantKey(v.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  variantKey === v.key
                    ? "bg-[var(--hmg-red)] text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {v.tab}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-500">{variant.hint}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto bg-white p-4 sm:p-6 rounded-xl shadow-sm">
        <OfficialChecklistForm d={doc} />
      </div>
    </main>
  );
}
